import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const statusConfig = {
  active: { color: 'bg-green-500/10 text-green-600 border-green-500/20', label: 'Active', icon: 'Zap' },
  scheduled: { color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', label: 'Scheduled', icon: 'Clock' },
  ended: { color: 'bg-gray-500/10 text-gray-500 border-gray-500/20', label: 'Ended', icon: 'MinusCircle' },
  sold: { color: 'bg-purple-500/10 text-purple-600 border-purple-500/20', label: 'Sold', icon: 'CheckCircle' },
  cancelled: { color: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Cancelled', icon: 'XCircle' },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig?.[status] || statusConfig?.ended;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      {cfg?.label}
    </span>
  );
};

const getTimeLeft = (endDate) => {
  if (!endDate) return '—';
  const diff = new Date(endDate) - new Date();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const ActiveBidsTable = ({ bids, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
        <div className="h-12 bg-muted/40 border-b border-border" />
        {[1, 2, 3]?.map(i => (
          <div key={i} className="h-16 border-b border-border/50 bg-muted/10" />
        ))}
      </div>
    );
  }

  if (!bids?.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Gavel" size={40} className="mx-auto text-muted-foreground mb-3" />
        <h3 className="text-base font-semibold text-foreground mb-1">No active bids</h3>
        <p className="text-muted-foreground text-sm">Head to the marketplace to start bidding</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Desktop Table Header */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-muted/30 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <div className="col-span-4">Item</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2 text-right">Your Bid</div>
        <div className="col-span-2 text-right">Current Bid</div>
        <div className="col-span-2 text-right">Time Left</div>
      </div>

      {bids?.map((bid, i) => {
        const auction = bid?.auction;
        const isWinning = bid?.is_winning;
        const isOutbid = !bid?.is_winning && (auction?.status === 'active' || auction?.status === 'scheduled');
        const timeLeft = getTimeLeft(auction?.end_date);
        const isUrgent = timeLeft !== '—' && timeLeft !== 'Ended' && !timeLeft?.includes('d');

        return (
          <div
            key={bid?.id || i}
            className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${isOutbid ? 'bg-red-500/5' : isWinning ? 'bg-green-500/5' : ''}`}
          >
            {/* Desktop Row */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
              {/* Item */}
              <div className="col-span-4 flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {auction?.featured_image_url ? (
                    <AppImage
                      src={auction?.featured_image_url}
                      alt={auction?.title || 'Auction item'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Package" size={16} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{auction?.title || 'Untitled Auction'}</p>
                  <p className="text-xs text-muted-foreground truncate">{auction?.seller?.full_name || 'Unknown Seller'}</p>
                </div>
              </div>
              {/* Status */}
              <div className="col-span-2">
                {isOutbid ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-red-500/10 text-red-500 border-red-500/20">
                    <Icon name="AlertTriangle" size={11} />
                    Outbid
                  </span>
                ) : isWinning ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-green-500/10 text-green-600 border-green-500/20">
                    <Icon name="Crown" size={11} />
                    Winning
                  </span>
                ) : (
                  <StatusBadge status={auction?.status} />
                )}
              </div>
              {/* Your Bid */}
              <div className="col-span-2 text-right">
                <span className="text-sm font-semibold text-foreground">
                  ${parseFloat(bid?.bid_amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {/* Current Bid */}
              <div className="col-span-2 text-right">
                <span className={`text-sm font-medium ${isWinning ? 'text-green-500' : 'text-foreground'}`}>
                  ${parseFloat(auction?.current_bid || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              {/* Time Left */}
              <div className="col-span-2 text-right">
                <span className={`text-sm font-medium ${isUrgent ? 'text-warning' : 'text-muted-foreground'}`}>
                  {isUrgent && <Icon name="Clock" size={12} className="inline mr-1" />}
                  {timeLeft}
                </span>
              </div>
            </div>

            {/* Mobile Card */}
            <div className="md:hidden px-4 py-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  {auction?.featured_image_url ? (
                    <AppImage
                      src={auction?.featured_image_url}
                      alt={auction?.title || 'Auction item'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Package" size={18} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-medium text-foreground truncate">{auction?.title || 'Untitled Auction'}</p>
                    {isOutbid ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-500 border border-red-500/20 flex-shrink-0">
                        <Icon name="AlertTriangle" size={10} />
                        Outbid
                      </span>
                    ) : isWinning ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20 flex-shrink-0">
                        <Icon name="Crown" size={10} />
                        Winning
                      </span>
                    ) : (
                      <StatusBadge status={auction?.status} />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{auction?.seller?.full_name || 'Unknown Seller'}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Your Bid</p>
                      <p className="text-sm font-semibold text-foreground">
                        ${parseFloat(bid?.bid_amount || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Current</p>
                      <p className={`text-sm font-medium ${isWinning ? 'text-green-500' : 'text-foreground'}`}>
                        ${parseFloat(auction?.current_bid || 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Time Left</p>
                      <p className={`text-sm font-medium ${isUrgent ? 'text-warning' : 'text-muted-foreground'}`}>
                        {timeLeft}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActiveBidsTable;
